import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import {
  TrendingUp,
  Users,
  Building2,
  Activity,
  DollarSign,
  FileText,
  BarChart3,
  PieChart,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  Wrench,
  Eye,
  Globe,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { AggregatesDiagnosticsTab } from '../components/analytics/AggregatesDiagnosticsTab';
import { UserAnalyticsTab } from '../components/analytics/UserAnalyticsTab';
import { WebsiteAnalyticsTab } from '../components/analytics/WebsiteAnalyticsTab';

interface PlatformAnalytics {
  overview: {
    totalCompanies: number;
    activeCompanies: number;
    totalUsers: number;
    activeUsers: number;
    totalCommissionVolume: number;
    growthRate: number;
  };
  companies: {
    byStatus: Record<string, number>;
    recentlyAdded: number;
    topByVolume: Array<{ id: string; name: string; volume: number }>;
  };
  users: {
    byRole: Record<string, number>;
    recentlyActive: number;
    loginTrend: Array<{ date: string; count: number }>;
  };
  activity: {
    uploadsLast30Days: number;
    commissionsProcessed: number;
    adminOperations: number;
    errorRate: number;
  };
  trends: {
    companiesGrowth: Array<{ month: string; count: number }>;
    usersGrowth: Array<{ month: string; count: number }>;
    revenueGrowth: Array<{ month: string; value: number }>;
  };
}

type TabType = 'overview' | 'user-analytics' | 'website' | 'diagnostics';

const Analytics: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [analytics, setAnalytics] = useState<PlatformAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async (showToast = false) => {
    try {
      if (showToast) setRefreshing(true);
      else setLoading(true);

      const getPlatformAnalytics = httpsCallable(functions, 'getPlatformAnalytics');
      const result = await getPlatformAnalytics({});

      setAnalytics(result.data as PlatformAnalytics);
      if (showToast) toast.success('Analytics refreshed');
    } catch (err: any) {
      console.error('Error fetching analytics:', err);
      toast.error(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-AU').format(value);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 p-6 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="text-white text-lg">Loading Analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen bg-slate-900 p-6 flex items-center justify-center">
        <p className="text-white">Failed to load analytics</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6 text-white">
      {/* Gradient Header */}
      <div className="relative overflow-hidden rounded-2xl mb-6">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 opacity-90"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full transform translate-x-32 -translate-y-32"></div>
        <div className="relative px-8 py-12">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white bg-opacity-20 rounded-xl">
                <BarChart3 className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-white">Platform Analytics</h1>
            </div>
            <button
              onClick={() => fetchAnalytics(true)}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
          <p className="text-white opacity-80 text-lg">
            Real-time platform metrics, trends, and business intelligence
          </p>
          <div className="mt-6 flex items-center gap-4 text-white opacity-70">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              <span className="text-sm">Live data</span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm">Growth tracking</span>
            </div>
            <div className="flex items-center gap-2">
              <PieChart className="w-4 h-4" />
              <span className="text-sm">Visual insights</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 p-1 bg-slate-800/50 backdrop-blur-sm rounded-xl w-fit border border-slate-700/50">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
            activeTab === 'overview'
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
              : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
          }`}
        >
          <BarChart3 className="w-4 h-4" />
          Overview
        </button>

        <button
          onClick={() => setActiveTab('user-analytics')}
          className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
            activeTab === 'user-analytics'
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
              : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
          }`}
        >
          <Eye className="w-4 h-4" />
          User Analytics
        </button>

        <button
          onClick={() => setActiveTab('website')}
          className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
            activeTab === 'website'
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
              : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
          }`}
        >
          <Globe className="w-4 h-4" />
          Website
        </button>

        <button
          onClick={() => setActiveTab('diagnostics')}
          className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
            activeTab === 'diagnostics'
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
              : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
          }`}
        >
          <Wrench className="w-4 h-4" />
          Diagnostics
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {/* Key Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Total Companies */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-6 border border-blue-500/50">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            {analytics.overview.growthRate !== 0 && (
              <div className={`flex items-center gap-1 ${analytics.overview.growthRate > 0 ? 'text-green-300' : 'text-red-300'}`}>
                {analytics.overview.growthRate > 0 ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                <span className="text-sm font-semibold">{Math.abs(analytics.overview.growthRate)}%</span>
              </div>
            )}
          </div>
          <div className="text-3xl font-bold mb-2">{formatNumber(analytics.overview.totalCompanies)}</div>
          <div className="text-blue-100 text-sm">Total Companies</div>
          <div className="mt-2 pt-2 border-t border-white/20">
            <span className="text-sm text-blue-100">{analytics.overview.activeCompanies} active</span>
          </div>
        </div>

        {/* Total Users */}
        <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl p-6 border border-purple-500/50">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div className="text-purple-100 text-sm">
              {((analytics.overview.activeUsers / analytics.overview.totalUsers) * 100).toFixed(0)}% active
            </div>
          </div>
          <div className="text-3xl font-bold mb-2">{formatNumber(analytics.overview.totalUsers)}</div>
          <div className="text-purple-100 text-sm">Total Users</div>
          <div className="mt-2 pt-2 border-t border-white/20">
            <span className="text-sm text-purple-100">{analytics.overview.activeUsers} active</span>
          </div>
        </div>

        {/* Commission Volume */}
        <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl p-6 border border-green-500/50">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="text-3xl font-bold mb-2">{formatCurrency(analytics.overview.totalCommissionVolume)}</div>
          <div className="text-green-100 text-sm">Total Commission Volume</div>
          <div className="mt-2 pt-2 border-t border-white/20">
            <span className="text-sm text-green-100">All-time total</span>
          </div>
        </div>

        {/* Activity */}
        <div className="bg-gradient-to-br from-orange-600 to-orange-700 rounded-xl p-6 border border-orange-500/50">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <Activity className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="text-3xl font-bold mb-2">{formatNumber(analytics.activity.uploadsLast30Days)}</div>
          <div className="text-orange-100 text-sm">Uploads (Last 30 Days)</div>
          <div className="mt-2 pt-2 border-t border-white/20">
            <span className="text-sm text-orange-100">{analytics.activity.commissionsProcessed} commissions</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Companies by Status */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <Building2 className="w-6 h-6 text-blue-400" />
            <h2 className="text-xl font-semibold">Companies by Status</h2>
          </div>
          <div className="space-y-3">
            {Object.entries(analytics.companies.byStatus).map(([status, count]) => {
              const percentage = (count / analytics.overview.totalCompanies) * 100;
              const statusColors: Record<string, string> = {
                active: 'bg-green-500',
                trial: 'bg-yellow-500',
                inactive: 'bg-gray-500',
                pending: 'bg-blue-500',
              };
              const barColor = statusColors[status] || 'bg-gray-500';

              return (
                <div key={status}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-300 capitalize">{status}</span>
                    <span className="text-sm font-semibold text-white">
                      {count} ({percentage.toFixed(0)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className={`${barColor} h-2 rounded-full transition-all duration-300`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-6 pt-6 border-t border-gray-700">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">Recently Added (7 days)</span>
              <span className="text-lg font-semibold text-green-400">+{analytics.companies.recentlyAdded}</span>
            </div>
          </div>
        </div>

        {/* Users by Role */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <Users className="w-6 h-6 text-purple-400" />
            <h2 className="text-xl font-semibold">Users by Role</h2>
          </div>
          <div className="space-y-3">
            {Object.entries(analytics.users.byRole).map(([role, count]) => {
              const percentage = (count / analytics.overview.totalUsers) * 100;
              const roleColors: Record<string, string> = {
                administrator: 'bg-purple-500',
                office_admin: 'bg-blue-500',
                broker: 'bg-green-500',
                super_admin: 'bg-red-500',
              };
              const barColor = roleColors[role] || 'bg-gray-500';

              return (
                <div key={role}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-300 capitalize">{role.replace('_', ' ')}</span>
                    <span className="text-sm font-semibold text-white">
                      {count} ({percentage.toFixed(0)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className={`${barColor} h-2 rounded-full transition-all duration-300`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-6 pt-6 border-t border-gray-700">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">Recently Active (7 days)</span>
              <span className="text-lg font-semibold text-green-400">{analytics.users.recentlyActive}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Top Companies by Volume */}
      {analytics.companies.topByVolume.length > 0 && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <TrendingUp className="w-6 h-6 text-green-400" />
            <h2 className="text-xl font-semibold">Top Companies by Commission Volume</h2>
          </div>
          <div className="space-y-3">
            {analytics.companies.topByVolume.map((company, index) => (
              <div key={company.id} className="flex items-center gap-4 p-4 bg-gray-900 rounded-lg border border-gray-700">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-green-600 font-bold text-lg">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-white">{company.name}</div>
                  <div className="text-sm text-gray-400">{company.id}</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-green-400">{formatCurrency(company.volume)}</div>
                  <div className="text-xs text-gray-400">Total Volume</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Activity Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <FileText className="w-8 h-8 text-blue-400" />
            <div className="text-sm text-gray-400">Last 30 days</div>
          </div>
          <div className="text-3xl font-bold mb-2">{formatNumber(analytics.activity.uploadsLast30Days)}</div>
          <div className="text-gray-400 text-sm">File Uploads</div>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <DollarSign className="w-8 h-8 text-green-400" />
            <div className="text-sm text-gray-400">Last 30 days</div>
          </div>
          <div className="text-3xl font-bold mb-2">{formatNumber(analytics.activity.commissionsProcessed)}</div>
          <div className="text-gray-400 text-sm">Commissions Processed</div>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <Activity className="w-8 h-8 text-purple-400" />
            <div className="text-sm text-gray-400">Last 30 days</div>
          </div>
          <div className="text-3xl font-bold mb-2">{formatNumber(analytics.activity.adminOperations)}</div>
          <div className="text-gray-400 text-sm">Admin Operations</div>
        </div>
      </div>

      {/* Growth Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Companies Growth */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <Building2 className="w-6 h-6 text-blue-400" />
            <h2 className="text-lg font-semibold">Companies Growth</h2>
          </div>
          <div className="space-y-2">
            {analytics.trends.companiesGrowth.map((item) => (
              <div key={item.month} className="flex items-center justify-between">
                <span className="text-sm text-gray-400">{item.month}</span>
                <span className="text-sm font-semibold text-blue-400">+{item.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Users Growth */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <Users className="w-6 h-6 text-purple-400" />
            <h2 className="text-lg font-semibold">Users Growth</h2>
          </div>
          <div className="space-y-2">
            {analytics.trends.usersGrowth.map((item) => (
              <div key={item.month} className="flex items-center justify-between">
                <span className="text-sm text-gray-400">{item.month}</span>
                <span className="text-sm font-semibold text-purple-400">+{item.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Revenue Growth */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <DollarSign className="w-6 h-6 text-green-400" />
            <h2 className="text-lg font-semibold">Revenue Trend</h2>
          </div>
          <div className="space-y-2">
            {analytics.trends.revenueGrowth.map((item) => (
              <div key={item.month} className="flex items-center justify-between">
                <span className="text-sm text-gray-400">{item.month}</span>
                <span className="text-sm font-semibold text-green-400">{formatCurrency(item.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
        </>
      )}

      {/* User Analytics Tab */}
      {activeTab === 'user-analytics' && <UserAnalyticsTab />}

      {/* Website Analytics Tab */}
      {activeTab === 'website' && <WebsiteAnalyticsTab />}

      {/* Diagnostics Tab */}
      {activeTab === 'diagnostics' && (
        <AggregatesDiagnosticsTab />
      )}
    </div>
  );
};

export default Analytics;
